/*
 *   This is a service for a specific store module which uses the api.ts Axios instance as a base.
 *   We separate our files for easy managment and readibility.
 *   We export this as a service to be used in other areas of the application.
 */

import Api from '@/services/api';
import AppService from '@/services/app';
import Store from '@/store/index';
import { LoadStop } from '@/models/Load.model';

export default { 

  resetMovementLoads() {
    // reset store to empty
    Store.dispatch('loads/setMovementLoads', {
      loads: [],
    });
  }, 

  resetApprovalLoads() {
    // reset store to empty
    Store.dispatch('loads/setApprovalLoads', {
      loads: [],
    });
  }, 

  resetCustomerRates() {
    // reset store to empty
    Store.dispatch('loads/setCustomerRates', {
      rates: [],
    });
  },

  async listMovementLoads(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['loads/movementLoads'].length < 1) {
      try {
        const response = await Api(true).get('/load/list_movement');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetMovementLoads();
            return response.data.message;
          } else {
            Store.dispatch('loads/setMovementLoads', {
              loads: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetMovementLoads();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('load.ts - listMovementLoads - 68 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  async listApprovalLoads(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['loads/approvalLoads'].length < 1) {
      try {
        const response = await Api(true).get('/load/list_approval');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetApprovalLoads();
            return response.data.message;
          } else {
            Store.dispatch('loads/setApprovalLoads', {
              loads: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetApprovalLoads();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('load.ts - listApprovalLoads - 104 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  updateStatus(params: { id: number, endKm: number, offloadId: number }) {
    return Api(true).post('/load/update_status', params);
  },

  create(params: { 
    loadDate: string,
    operatorId: number,
    orderNumber?: string,
    customerId: number,
    from: string, 
    to: string,
    rate: number,
    driverId?: number,
    horseVehicleId?: number,
    startKm?: number,
    trailer1VehicleId?: number,
    trailer2VehicleId?: number,
    isPriorityLoad: boolean,
    comment?: string,
  }) {
    return Api(true).post('/load/create', params);
  },

  updateMovement(params: { 
    id: number,
    orderNumber?: string,
    customerId: number,
    from: string,
    to: string,
    rate: number, 
    driverId?: number,
  }) {
    return Api(true).post('/load/update_movement', params);
  },

  updateApproval(params: {
    id: number,
    orderNumber: string,
    customerId: number,
    from: string, 
    to: string,
    rate: number,
    driverId: number,
    horseVehicleId: number,
    startKm: number,
    trailer1VehicleId: number,
    trailer2VehicleId?: number,
    endKm: number,
  }) {
    return Api(true).post('/load/update_approval', params);
  },

  updatePriority(params: { id: number, isPriorityLoad: boolean }) {
    return Api(true).post('/load/update_priority', params);
  },

  delete(params: { id: number }) {
    return Api(true).post('/load/delete', params);
  },

  approve(params: { id: number }) {
    return Api(true).post('/load/approve', params);
  },

  updateOrderNumber(params: { id: number, orderNumber: string }) {
    return Api(true).post('/load/update_order_number', params);
  },

  removeOrderNumber(params: { id: number }) {
    return Api(true).post('/load/remove_order_number', params);
  },

  reject(params: { 
    loadId: number, 
    comment: string, 
  }) {
    return Api(true).post('/load/reject', params);
  },

  getLoad(config: {params: { loadId: number }}) {
    return Api(true).get('/load/get', config);
  },

  offHookTrailer(params: { 
    id: number,
    swapLocation: string,
    endKm: number,
    comment?: string,
  }) {
    return Api(true).post('/load/offhook_trailer', params);
  },

  changeHorse(params: {
    id: number,
    swapLocation: string,
    oldHorseEndKm: number,
    newHorseVehicleId: number,
    newHorseStartKm: number,
    newDriverId: number,
    reasonForChange?: string,
  }) {
    return Api(true).post('/load/change_horse', params);
  },

  updateTrailers(params: {
    id: number,
    trailer1VehicleId: number,
    trailer2VehicleId?: number,
  }) {
    return Api(true).post('/load/update_trailers', params);
  },

  listOffloadsByLoad(config: {params: { loadId: number }}) {
    return Api(true).get('/offloads/list_by_load', config);
  },

  createOffloads(params: {
    loadId: number,
    offloads: LoadStop[],
  }) {
    return Api(true).post('/offloads/create', params);
  },

  search(config: {params: { 
    customerId: number,
    statusIds: string,
    vehicleId: number,
    loadNumber: string,
    invoiceNumber: string,
    startDate: string,
    endDate: string,
  }}) {
    return Api(true).get('/load/search', config);
  },

  forcedDownload(config: {params: { 
    customerId: number,
    statusIds: string,
    vehicleId: number,
    loadNumber: string,
    invoiceNumber: string,
    startDate: string,
    endDate: string,
  }}) {
    return Api(true).get('/load/forced_download', config);
  },

};
